import { useState, useEffect, useCallback } from 'react';
import { usePharma } from '../../../providers/PharmaProvider';
import { ClinicDevice } from '../interfaces/participantTrackerTypes';
import type { SptDataSource } from '../types';

const API_URL = 'https://dev.seevividly.com/api_clinical_trials/get_clinic_devices';

export const useClinicDevices = (sptSource: SptDataSource = 'production') => {
  const { user } = usePharma();
  const [data, setData] = useState<ClinicDevice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!user?.user_name || !user?.p) {
      setData([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          u: user.user_name,
          p: user.p,
          ...(sptSource !== 'production' && { spt_source: sptSource }),
        }),
      });

      const responseText = await response.text();
      if (!response.ok) {
        console.error("Clinic Devices API Error Response:");
        throw new Error(`Clinic Devices API request failed: ${response.status} ${response.statusText || ''}.`);
      }

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError: any) {
        console.error("Failed to parse Clinic Devices API response:");
        throw new Error(`Invalid JSON received from Clinic Devices API. ${parseError.message}`);
      }
      
      if (result?.status === 'success' && result.data && Array.isArray(result.data.data)) {
        setData(result.data.data as ClinicDevice[]);
      } else {
        console.error('Unexpected Clinic Devices API response format:');
        setError('Received invalid data format from Clinic Devices API.');
        setData([]);
      }
    } catch (err: any) {
      console.error('Fetch clinic devices error:');
      setError(err.message || 'Failed to load clinic device data.');
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [user, sptSource]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
};
